import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Redirect, useHistory } from 'react-router';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import useSellerQueries from '@dashboard/hooks/queries/useSellerQueries';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import { useEffect } from 'react';
import { apiUrl, stripePublicKey } from '@dashboard/services/env';
import request, { fetcher } from '@dashboard/services/request';
import { Formik } from 'formik';
import { format } from 'date-fns';
import { FormElement, Label } from '@dashboard/components/Form';
import { FormSubmit, Select, TextInput } from '@dashboard/components/Formik';
import { countries, isEU } from '@dashboard/services/countries/countries';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import Spinner from '@dashboard/components/Spinner';
import { getPlan } from '@dashboard/services/plans';
import hasLiveSubscription from '@dashboard/services/hasLiveSubscription';
import { formatPrice } from '@dashboard/services/formatting';
import { toast } from '@dashboard/context/ToastContext';
import {
  sellerBillingAddress,
  sellerKey,
  sellerPaymentMethodKey,
  sellerUsageKey,
} from '@dashboard/lib/queryKeys';
import getCouponDiscount from './getCouponDiscount';
import { stringify } from 'query-string';
import useRewardful from '@dashboard/hooks/useRewardful';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { isEmpty } from 'lodash';
import { tracking } from '../../../../lib/tracking';
import scaleBrackets from '@dashboard/services/scaleBrackets';
import Button from '@dashboard/components/Button';
import { Alert, AlertTitle, AlertDescription } from '@/dashboard/components/ui/alert';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Check, ChevronLeft } from 'lucide-react';
import SwitchToYearlyOffer from './SwitchToYearlyOffer';
import SectionHeading from '@dashboard/components/SectionHeading';
import CardBrandIcon from '@dashboard/components/CardBrandIcon';
import useTheme from '@dashboard/hooks/useTheme';
import getCardElementOptions from '@dashboard/lib/cardElementOptions';

const FREE_PLAN_FEATURES = [
  'Essential support',
  '1 team member',
  'Unlimited checkout pages, events, and forms',
  'Checkout Page branding',
];

const FREE_PLAN_UNLOCKS = [
  { category: 'Team', items: 'unlimited team members, event staff' },
  { category: 'Customization', items: 'remove Checkout Page branding, custom CSS' },
  {
    category: 'Revenue boosters',
    items:
      'one-click upsells, cart abandonment recovery, bulk discounts, dynamic pricing, custom fees',
  },
  {
    category: 'Domains & integrations',
    items: 'custom domain, custom email domain, tracking pixels',
  },
];

const Upgrade = () => {
  const { plan: planHandle, billing_period: billingPeriod, scaleBracket } = useUrlQuery();
  const { seller } = useAuth();

  const plan = getPlan(planHandle);

  if (!plan) {
    return <Redirect to="/settings/plan" />;
  }

  if (plan.handle !== 'free' && hasLiveSubscription(seller)) {
    return (
      <Redirect
        to={`/settings/plan?${stringify({
          plan: planHandle,
          billing_period: plan.monthlyPrice === null ? 'year' : billingPeriod,
          scaleBracket: Number(scaleBracket) || undefined,
        })}`}
      />
    );
  }

  return <UpgradePlanSteps />;
};

export function PriceBreakdown({ plan, coupon, billingPeriod, scaleBracket }) {
  // For Scale plans, use bracket pricing, otherwise use plan pricing
  const isScalePlan = plan?.handle === 'scale';
  const bracket =
    isScalePlan && scaleBracket ? scaleBrackets.find(b => b.value === scaleBracket) : null;

  const displayPlan =
    isScalePlan && bracket
      ? {
          ...plan,
          monthlyPrice: bracket.monthlyPrice,
          yearlyPrice: bracket.yearlyPrice,
          yearlyPriceFull: bracket.yearlyPriceFull,
        }
      : plan;

  const monthlyCouponDiscount = getCouponDiscount(displayPlan?.monthlyPrice, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const yearlyCouponDiscount = getCouponDiscount(displayPlan?.yearlyPriceFull, {
    amountOff: coupon?.amount_off,
    percentOff: coupon?.percent_off,
  });

  const discountedMonthlyPrice = displayPlan?.monthlyPrice - monthlyCouponDiscount;

  const initialYearlyPrice = displayPlan?.monthlyPrice * 12;
  const yearlyPrice = displayPlan?.yearlyPriceFull;
  const discountedYearlyPrice = yearlyPrice - yearlyCouponDiscount;

  const salesLimit = bracket
    ? bracket.label.replace(' sales per month', '')
    : plan.handle === 'launch' || plan.handle === 'free'
      ? 'Up to $3K'
      : plan.handle === 'scale'
        ? 'Up to $30K'
        : plan.handle === 'unlimited'
          ? 'Unlimited'
          : '';

  const isFree = plan?.handle === 'free';
  const isYear = !isFree && billingPeriod === 'year';

  const couponDurationLabel = !coupon
    ? ''
    : coupon.duration === 'once'
      ? ' (one-time)'
      : coupon.duration === 'forever'
        ? isYear
          ? ' / year'
          : ' / month'
        : coupon.duration === 'repeating'
          ? ` (${coupon.duration_in_months} months)`
          : '';

  const period = isYear ? 'year' : 'month';
  const headlineAmount = isYear ? yearlyPrice : displayPlan.monthlyPrice;
  const discountAmount = isYear ? yearlyCouponDiscount : monthlyCouponDiscount;
  const totalAmount = isYear ? discountedYearlyPrice : discountedMonthlyPrice;

  return (
    <div
      className={`w-full rounded-lg border border-input bg-card p-5 text-left ${isFree ? '' : 'mb-6'}`}
    >
      <div className="flex items-baseline justify-between gap-4">
        <div className="text-2xl font-semibold leading-none text-gray-900 dark:text-foreground">
          {formatPrice({ amount: headlineAmount, currency: 'USD' })}
          <span className="text-base font-normal text-gray-500 dark:text-muted-foreground">
            {' '}
            / {period}
          </span>
        </div>

        {isYear && displayPlan?.monthlyPrice != null && (
          <div className="shrink-0 text-sm text-gray-400 dark:text-muted-foreground line-through">
            {formatPrice({ amount: initialYearlyPrice, currency: 'USD' })} / year
          </div>
        )}
      </div>

      {isFree && (
        <p className="mt-1.5 text-sm text-gray-600 dark:text-muted-foreground">
          You only pay Stripe's standard fees
        </p>
      )}

      {salesLimit && (
        <div className="mt-4 border-t border-input pt-3">
          <SectionHeading>Sales limit</SectionHeading>
          <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1.5">
            <span className="font-semibold text-gray-900 dark:text-foreground">{salesLimit}</span>
            {salesLimit !== 'Unlimited' && ' in monthly sales'}
          </p>
        </div>
      )}

      {isFree && (
        <>
          <div className="mt-4 border-t border-input pt-3">
            <SectionHeading>Features included</SectionHeading>
            <ul className="flex flex-col gap-y-2 mt-2">
              {FREE_PLAN_FEATURES.map(feature => (
                <li
                  key={feature}
                  className="flex items-start gap-x-2 text-sm text-gray-800 dark:text-foreground"
                >
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground">
                    <Check className="h-3 w-3 text-background" strokeWidth={3} />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4 border-t border-input pt-3">
            <SectionHeading>Upgrade to unlock</SectionHeading>
            <ul className="flex flex-col gap-y-1.5 mt-2 text-sm text-gray-600 dark:text-muted-foreground">
              {FREE_PLAN_UNLOCKS.map(({ category, items }) => (
                <li key={category}>
                  <span className="font-medium text-gray-900 dark:text-foreground">
                    {category}:
                  </span>{' '}
                  {items}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {coupon && !isFree && (
        <div className="mt-4 space-y-1.5 border-t border-input pt-4 text-sm">
          <div className="flex items-center justify-between text-gray-600 dark:text-muted-foreground">
            <span>Discount</span>
            <span>
              {formatPrice({ amount: discountAmount, currency: 'USD' })}
              {couponDurationLabel}
            </span>
          </div>
        </div>
      )}

      {!isFree && (
        <div
          className={`mt-4 flex items-center justify-between border-t border-input ${coupon ? 'pt-3' : 'pt-4'} text-sm`}
        >
          <span className="text-gray-500 dark:text-muted-foreground">
            {isYear ? 'Billed annually' : 'Billed monthly'} · cancel anytime
          </span>
          <span className="font-semibold text-gray-900 dark:text-foreground">
            {formatPrice({ amount: totalAmount, currency: 'USD' })} due today
          </span>
        </div>
      )}
    </div>
  );
}

const UpgradePlanSteps = () => {
  const { sellerId, token, seller } = useAuth();
  const {
    plan: planHandle,
    billing_period: billingPeriodQuery,
    coupon: couponQuery,
    upgradeFlow: upgradeFlowQuery,
    scaleBracket,
  } = useUrlQuery();

  const [billingPeriod, setBillingPeriod] = useState(billingPeriodQuery || 'month');
  const [upgradeFlow, setUpgradeFlow] = useState(upgradeFlowQuery || 'upgrade-page');

  const upgradePlan = getPlan(planHandle);

  const { coupon: rewardfulCoupon } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  function switchBillingPeriodToYearly() {
    setBillingPeriod('year');
    setUpgradeFlow('upgrade-page-yearly-nudge');
  }

  return (
    <div className="max-w-xl mx-auto px-4">
      <div className={`text-center ${upgradePlan.handle === 'free' ? 'mb-4' : 'mb-8'}`}>
        <h1 className="text-xl font-bold mb-6">
          {upgradePlan.handle === 'free'
            ? seller?.subscriptionStatus === 'active'
              ? 'Switch to Free'
              : 'Start for free'
            : `Upgrade to ${upgradePlan.title}`}
        </h1>

        <PriceBreakdown
          plan={upgradePlan}
          coupon={coupon}
          billingPeriod={billingPeriod}
          scaleBracket={Number(scaleBracket)}
        />

        {upgradePlan.handle !== 'free' && billingPeriod === 'month' && (
          <SwitchToYearlyOffer
            plan={upgradePlan}
            scaleBracket={Number(scaleBracket)}
            onSwitch={switchBillingPeriodToYearly}
          />
        )}
      </div>

      <UpgradeForm
        onSuccessPath="/settings/plan"
        planHandle={planHandle}
        billingPeriod={billingPeriod}
        couponQuery={couponQuery}
        upgradeFlow={upgradeFlow}
        scaleBracket={Number(scaleBracket)}
      />
    </div>
  );
};

export const UpgradeForm = ({
  onSuccessPath,
  planHandle,
  billingPeriod,
  couponQuery,
  upgradeFlow,
  scaleBracket,
}) => {
  const { sellerId, isAuthenticating, token, setSeller, user, seller } = useAuth();
  const { appliedTheme } = useTheme();
  const { getSellerAddress } = useSellerQueries();
  const queryClient = useQueryClient();
  const history = useHistory();
  const stripe = useStripe();
  const elements = useElements();
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);

  const [shouldPollSeller, setShouldPollSeller] = useState(false);

  const upgradePlan = getPlan(planHandle);

  const { coupon: rewardfulCoupon, referral: rewardfulReferral } = useRewardful();

  const { data: coupon } = useQuery({
    queryKey: ['coupon', couponQuery, rewardfulCoupon, sellerId],
    queryFn: () =>
      fetcher(
        `/sellers/${sellerId}/validate-coupon?coupon=${couponQuery || rewardfulCoupon.id}`,
        token,
      ),
    enabled: !!sellerId && (!!couponQuery || !!rewardfulCoupon),
    retry: false,
  });

  // Free has no Stripe subscription - selecting it switches the seller to the Free
  // plan via the downgrade-to-free endpoint (cancels any active/trial sub, drops to
  // 1 seat, detaches custom domain). No card or billing details required.
  const startFreeMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/downgrade-to-free`, token, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: async updatedSeller => {
      setSeller(updatedSeller);
      await queryClient.invalidateQueries({ queryKey: sellerUsageKey(sellerId) });

      if (updatedSeller.pendingFreeDowngrade && updatedSeller.stripeSubscriptionCancelAt) {
        toast({
          title: 'Switch to Free scheduled',
          description: `Your paid plan stays active until ${format(
            new Date(updatedSeller.stripeSubscriptionCancelAt),
            'd MMM, yyyy',
          )}, then your account switches to Free.`,
          type: 'success',
        });
      } else {
        toast({
          title: "You're on Free",
          description: 'Your account is now on the Free plan.',
          type: 'success',
        });
      }

      history.push(onSuccessPath);
    },
    onError: () => {
      toast({
        title: 'Something went wrong',
        description: 'Could not switch to the Free plan. Please try again.',
        type: 'error',
      });
    },
  });

  const { data: address, isLoading } = useQuery({
    queryKey: sellerBillingAddress(sellerId),
    queryFn: () => getSellerAddress(sellerId),
    enabled: !!sellerId,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
  });

  const { data: storedCard, isLoading: isLoadingStoredCard } = useQuery({
    queryKey: sellerPaymentMethodKey(sellerId),
    queryFn: async () => {
      try {
        const paymentMethod = await fetcher(`/sellers/${sellerId}/payment-method`, token);
        return paymentMethod?.card ? paymentMethod : null;
      } catch {
        return null;
      }
    },
    enabled: !!sellerId,
    retry: false,
    // Refetch on every mount: the card can be replaced (and the old one
    // detached) under Billing between visits. No refocus refetch - the
    // card must not swap mid-checkout.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const payingWithStoredCard = Boolean(storedCard) && !useNewCard;

  const { data: updatedSeller } = useQuery({
    queryKey: [...sellerKey(sellerId), 'updatedSeller'],
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
    enabled: !!shouldPollSeller,
    refetchInterval: 1000,
  });

  useEffect(() => {
    if (
      updatedSeller?.stripeSubscriptionId &&
      updatedSeller?.plan?.handle === upgradePlan.handle &&
      !updatedSeller?.stripeSubscriptionCancelAt
    ) {
      setSeller(updatedSeller);

      toast({
        title: "You're subscribed!",
        description: `Your subscription has been updated to the ${upgradePlan.title} plan`,
        type: 'success',
      });

      const sellerBillingPeriod = updatedSeller?.planBillingPeriod || 'month';

      let monthlyPrice =
        sellerBillingPeriod === 'month' ? upgradePlan.monthlyPrice : upgradePlan.yearlyPrice;

      if (monthlyPrice == null) monthlyPrice = upgradePlan.yearlyPriceFull / 12;

      if (scaleBracket && upgradePlan.handle === 'scale') {
        if (sellerBillingPeriod === 'month') {
          monthlyPrice = scaleBrackets.find(b => b.value === scaleBracket).monthlyPrice;
        } else {
          monthlyPrice = scaleBrackets.find(b => b.value === scaleBracket).yearlyPrice;
        }
      }

      // convert to dollars, 0 decimal places, keep as number
      monthlyPrice = Math.round(monthlyPrice / 100);

      tracking.trackEvent('Subscribe', {
        currency: 'USD',
        predicted_ltv: 600,
        monthlyPrice,
        subscriptionId: updatedSeller.stripeSubscriptionId,
        plan: updatedSeller.plan?._id,
        planName: upgradePlan.title,
        email: user?.email,
        gclid: updatedSeller.gclid,
      });

      history.push(onSuccessPath);
    }
  }, [updatedSeller]);

  if (isAuthenticating) {
    return null;
  }

  if (upgradePlan?.handle === 'free') {
    const losesActiveSubscription = seller?.subscriptionStatus === 'active';
    const losesCustomDomain = seller?.customDomain && seller?.customDomainVerified;

    return (
      <div>
        {losesActiveSubscription && (
          <Alert variant="warning" className="mb-4">
            <AlertDescription>
              At the end of your current billing period your paid subscription ends and your team is
              reduced to 1 member
              {losesCustomDomain
                ? `, and your custom domain (${seller.customDomain}) is disconnected`
                : ''}
              . Your pages, products, and data stay intact.
            </AlertDescription>
          </Alert>
        )}

        <Alert className="mb-6 text-left">
          <InformationCircleIcon />
          <AlertTitle>Your $3K monthly sales limit</AlertTitle>
          <AlertDescription>
            This plan includes up to $3,000 in monthly sales. Once you reach the limit, new payments
            are paused until your limit resets next month or you upgrade.
          </AlertDescription>
        </Alert>

        <Button
          appearance="primary"
          size="full"
          onClick={() => startFreeMutation.mutate()}
          disabled={startFreeMutation.isLoading}
        >
          {startFreeMutation.isLoading
            ? 'Switching…'
            : losesActiveSubscription
              ? 'Switch to Free'
              : 'Start for free'}
        </Button>

        <p className="mt-3 text-center text-sm text-gray-500 dark:text-muted-foreground">
          No credit card required. Upgrade anytime to increase your sales limit.
        </p>
      </div>
    );
  }

  async function confirmSubscriptionPayment(clientSecret) {
    const { error } = await stripe.confirmCardPayment(
      clientSecret,
      payingWithStoredCard
        ? { payment_method: storedCard.id }
        : { payment_method: { card: elements.getElement(CardElement) } },
    );

    if (error) {
      throw error;
    }
  }

  async function confirmSubscriptionSetup(setupIntentClientSecret) {
    const { error } = await stripe.confirmCardSetup(
      setupIntentClientSecret,
      payingWithStoredCard
        ? { payment_method: storedCard.id }
        : { payment_method: { card: elements.getElement(CardElement) } },
    );

    if (error) {
      throw error;
    }
  }

  const dueTodayBracket =
    upgradePlan?.handle === 'scale' && scaleBracket
      ? scaleBrackets.find(b => b.value === scaleBracket)
      : null;
  const dueTodayFull =
    billingPeriod === 'year'
      ? dueTodayBracket
        ? dueTodayBracket.yearlyPriceFull
        : upgradePlan?.yearlyPriceFull
      : dueTodayBracket
        ? dueTodayBracket.monthlyPrice
        : upgradePlan?.monthlyPrice;
  const dueToday =
    dueTodayFull -
    getCouponDiscount(dueTodayFull, {
      amountOff: coupon?.amount_off,
      percentOff: coupon?.percent_off,
    });

  // check if the address country is in the countries array
  // otherwise format may be incorrect, so we let user fix in the address form
  const adressCountry = countries.find(country => country.alpha2 === address?.country);

  const isAddressIncomplete =
    !address ||
    !address?.firstName ||
    !address?.lastName ||
    !address?.streetAddress ||
    !address?.city ||
    !address?.postalCode ||
    !address?.country ||
    !adressCountry;

  return (
    <>
      {(isLoading || isLoadingStoredCard) && (
        <div className="flex justify-center">
          <Spinner />
        </div>
      )}

      {!isLoading && !isLoadingStoredCard && (
        <Formik
          enableReinitialize
          initialValues={{
            firstName: address?.firstName,
            lastName: address?.lastName,
            companyName: address?.companyName,
            streetAddress: address?.streetAddress,
            streetAddress2: address?.streetAddress2,
            city: address?.city,
            state: address?.state,
            postalCode: address?.postalCode,
            country: address?.country,
            vatNumber: address?.vatNumber,
          }}
          onSubmit={async (values, actions) => {
            actions.setStatus('');

            try {
              if (!clientSecret && !setupIntentClientSecret) {
                const result = await request(
                  apiUrl + `/sellers/${sellerId}/start-subscription`,
                  token,
                  {
                    body: JSON.stringify({
                      ...values,
                      plan: upgradePlan.handle,
                      billingPeriod: billingPeriod,
                      referral: rewardfulReferral,
                      ...(coupon && { coupon: coupon.id }),
                      upgradeFlow,
                      ...(scaleBracket &&
                        upgradePlan.handle === 'scale' && { bracket: scaleBracket }),
                    }),
                    method: 'POST',
                  },
                );

                if (!result.data) {
                  throw result;
                }

                if (result.data.clientSecret) {
                  setClientSecret(result.data.clientSecret);

                  await confirmSubscriptionPayment(result.data.clientSecret);
                }

                if (result.data.setupIntentClientSecret) {
                  setSetupIntentClientSecret(result.data.setupIntentClientSecret);

                  await confirmSubscriptionSetup(result.data.setupIntentClientSecret);
                }
              } else {
                if (clientSecret) {
                  await confirmSubscriptionPayment(clientSecret);
                }

                if (setupIntentClientSecret) {
                  await confirmSubscriptionSetup(setupIntentClientSecret);
                }
              }

              setShouldPollSeller(true);
            } catch (err) {
              // A Stripe error (always typed) on the saved card: reveal the card
              // input so a new card can retry the same intent.
              if (payingWithStoredCard && err?.type) {
                setUseNewCard(true);
                actions.setStatus(
                  `We couldn't charge your saved card: ${err.message} Enter a different card to continue.`,
                );
                actions.setSubmitting(false);
                return;
              }

              const { status, errors } = transformApiErrorToFormik(err);

              if (!isEmpty(errors)) {
                actions.setErrors(errors);
              } else {
                actions.setStatus(status);
              }

              actions.setSubmitting(false);
            }
          }}
        >
          {({ values, status, errors, handleSubmit }) => {
            // if there are errors that are not in the fields, show the first one
            const errorsWithoutField = Object.entries(errors)
              .filter(([key]) => !fieldsArray?.find(field => field.name === key))
              .map(([, value]) => value);

            const firstError = errorsWithoutField.length ? errorsWithoutField[0] : undefined;

            return (
              <form onSubmit={handleSubmit} autoComplete>
                {isAddressIncomplete && (
                  <>
                    <div style={{ display: 'flex' }}>
                      <FormElement style={{ marginRight: '1rem', flex: '1' }}>
                        <Label htmlFor="firstName">First name</Label>
                        <TextInput
                          name="firstName"
                          id="firstName"
                          type="text"
                          placeholder="First name"
                          required
                          border
                        />
                        <FormError name="firstName" />
                      </FormElement>

                      <FormElement style={{ flex: '1' }}>
                        <Label htmlFor="lastName">Last name</Label>
                        <TextInput
                          name="lastName"
                          id="lastName"
                          type="text"
                          placeholder="Last name"
                          required
                          border
                        />
                        <FormError name="lastName" />
                      </FormElement>
                    </div>

                    <FormElement>
                      <Label htmlFor="companyName" optional>
                        Company name
                      </Label>
                      <TextInput
                        name="companyName"
                        id="companyName"
                        type="text"
                        placeholder="Company name"
                        border
                      />
                      <FormError name="companyName" />
                    </FormElement>

                    <FormElement>
                      <Label htmlFor="streetAddress">Address line 1</Label>
                      <TextInput
                        name="streetAddress"
                        id="streetAddress"
                        type="text"
                        placeholder="Address line 1"
                        required
                        border
                      />
                      <FormError name="streetAddress" />
                    </FormElement>

                    <FormElement>
                      <Label htmlFor="streetAddress2" optional>
                        Address line 2
                      </Label>
                      <TextInput
                        name="streetAddress2"
                        id="streetAddress2"
                        type="text"
                        placeholder="Address line 2"
                        border
                      />
                      <FormError name="streetAddress2" />
                    </FormElement>

                    <div style={{ display: 'flex' }}>
                      <FormElement style={{ marginRight: '1rem', flex: '1' }}>
                        <Label htmlFor="country">Country</Label>

                        <Select name="country" id="country" border placeholder="Country" required>
                          <option>Select country</option>

                          {countries.map(country => (
                            <option value={country.alpha2} key={country.alpha2}>
                              {country.name}
                            </option>
                          ))}
                        </Select>
                        <FormError name="country" />
                      </FormElement>

                      <FormElement style={{ flex: '1' }}>
                        <Label htmlFor="city">City</Label>
                        <TextInput
                          name="city"
                          id="city"
                          type="text"
                          placeholder="City"
                          required
                          border
                        />
                        <FormError name="city" />
                      </FormElement>
                    </div>

                    <div style={{ display: 'flex' }}>
                      <FormElement style={{ marginRight: '1rem', flex: '1' }}>
                        <Label htmlFor="state" optional>
                          State
                        </Label>
                        <TextInput name="state" id="state" type="text" placeholder="State" border />
                        <FormError name="state" />
                      </FormElement>

                      <FormElement style={{ flex: '1' }}>
                        <Label htmlFor="postalCode">Postal code / Zip</Label>
                        <TextInput
                          name="postalCode"
                          id="postalCode"
                          type="text"
                          placeholder="Postal code / Zip"
                          required
                          border
                        />
                        <FormError name="postalCode" />
                      </FormElement>
                    </div>

                    {values.country && isEU(values.country) && (
                      <FormElement>
                        <Label htmlFor="vatNumber">VAT Number</Label>
                        <TextInput
                          name="vatNumber"
                          id="vatNumber"
                          type="text"
                          placeholder="VAT Number"
                          border
                        />
                        <FormError name="vatNumber" />
                      </FormElement>
                    )}
                  </>
                )}

                {payingWithStoredCard && (
                  <FormElement>
                    <Label>Card</Label>
                    <div className="flex w-full items-center gap-3 rounded bg-white dark:bg-background py-2 px-2 shadow-input">
                      <CardBrandIcon brand={storedCard.card.brand} />
                      <span className="min-w-0 truncate text-[16px] leading-normal text-[#382b2f] dark:text-foreground">
                        •••• {storedCard.card.last4}
                      </span>
                      <span className="whitespace-nowrap text-[16px] leading-normal text-gray-400 dark:text-muted-foreground">
                        {String(storedCard.card.exp_month).padStart(2, '0')} /{' '}
                        {String(storedCard.card.exp_year).slice(-2)}
                      </span>
                      <button
                        type="button"
                        className="ml-auto whitespace-nowrap text-sm font-medium text-gray-500 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground"
                        onClick={() => setUseNewCard(true)}
                      >
                        Use a different card
                      </button>
                    </div>
                  </FormElement>
                )}

                {!payingWithStoredCard && (
                  <FormElement>
                    <Label htmlFor="stripe-card-element">Card</Label>
                    <CardElement
                      id="stripe-card-element"
                      options={getCardElementOptions(appliedTheme)}
                      className="bg-white dark:bg-background placeholder-gray-200 border-none shadow-input focus:shadow-input-focus appearance-none block w-full py-2 px-2 rounded"
                    />
                  </FormElement>
                )}

                {status && typeof status === 'string' && <FormErrorBase>{status}</FormErrorBase>}
                {firstError && typeof firstError === 'string' && (
                  <FormErrorBase>{firstError}</FormErrorBase>
                )}

                <FormSubmit>
                  <FormSubmit.Button appearance="primary" size="full" disabled={shouldPollSeller}>
                    Start subscription
                  </FormSubmit.Button>
                </FormSubmit>

                <p className="mt-3 text-center text-sm text-gray-500 dark:text-muted-foreground">
                  Billed {formatPrice({ amount: dueToday, currency: 'USD' })} today, then{' '}
                  {billingPeriod === 'year' ? 'yearly' : 'monthly'}. Cancel anytime.
                </p>
              </form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const ElementsWrapper = ({ children }) => {
  return (
    <Elements stripe={loadStripe(stripePublicKey)} options={{}}>
      {children}
    </Elements>
  );
};

export default function UpgradeContainer(props) {
  const {
    plan: planHandle,
    billing_period: billingPeriod,
    coupon: couponQuery,
    upgradeFlow,
    scaleBracket,
  } = useUrlQuery();

  const backHref = `/settings/plan?${stringify({
    plan: planHandle,
    billing_period: billingPeriod,
    coupon: couponQuery,
    upgradeFlow,
    ...(scaleBracket && { scaleBracket }),
  })}`;

  return (
    <ElementsWrapper>
      <div className="mb-2">
        <Button appearance="light" to={backHref} aria-label="Back">
          <ChevronLeft className="w-5 h-5" />
        </Button>
      </div>

      <Upgrade {...props} />
    </ElementsWrapper>
  );
}
